import { Job } from '@/apis/recruitementApis'
import iconComponents from '@/assets/icons/iconComponents'
import Button from '@/common/button'
import { ToastNotify } from '@/common/toastManager'
import Typography from '@/common/Typography'
import { withAsync } from '@/helpers/withAsync'
import useQueryParams from '@/hooks/useQueryParams'
import { JobImages } from '@/pages/recruitment/recruiter/jobsposting/detail/JobDescription'
import { useAppSelector } from '@/store/hooks'
import {
  useAlumniApplyForJobMutation,
  useGetJobQuery,
  useSubScribeToRecruiterJobMutation,
} from '@/store/recruitementSlice'
import clsx from 'clsx'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { JobTypeStyles } from '../../components/JobBoard'
import { Tooltip } from 'react-tooltip'

const JobDetail = (props: {
  className?: string
  hasApplied?: boolean
  job?: Job
}) => {
  const { hasApplied, job } = props
  const jobId = useQueryParams().job

  const selectedId =
    useAppSelector((state) => state.jobs.selectedJob) || Number(jobId)
  const { data: fullJob } = useGetJobQuery(selectedId, {
    skip: !!job || !selectedId,
  })
  return (
    <section
      className={clsx(
        'w-full flex flex-1  items-start gap-2.5 p-[16px_4px_0px_16px] overflow-y-auto  scrollbar scrollbar-w-1 scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg scrollbar-thumb-jaa-dark-teal-80 scrollbar-track-jaa-shades-white',
        props.className || ''
      )}
    >
      <div className='w-full'>
        {selectedId || job ? (
          <div className='flex flex-col items-end gap-5 w-full h-full flex-1 '>
            <JobInfo
              key={fullJob?.id}
              hasApplied={hasApplied}
              job={job || fullJob}
            />
            <Typography
              as='p'
              className='font-normal sm text-black flex-1 !text-left w-full'
            >
              {(job || fullJob)?.description}
            </Typography>
          </div>
        ) : (
          <NoDetailSelected />
        )}
      </div>
    </section>
  )
}

const JobInfo = (props: { hasApplied?: boolean; job?: Job }) => {
  const { t } = useTranslation()

  const { hasApplied, job } = props
  const [subScribeToJob] = useSubScribeToRecruiterJobMutation()
  const [isApplied, setIsApplied] = useState(hasApplied)
  const [applyForJob, { isLoading }] = useAlumniApplyForJobMutation()
  const applyHandler = async () => {
    if (!job?.id || hasApplied) return
    const { error, response } = await withAsync(() =>
      applyForJob(job.id).unwrap()
    )
    if (error) {
      ToastNotify('error', {
        message: (error as any).message || t('app.job_board.apply_error_msg'),
      })
      return
    }

    ToastNotify('success', {
      message:
        (response as any)?.message || t('app.job_board.apply_success_msg'),
    })
    setIsApplied(true)
  }
  useEffect(() => {
    setIsApplied(hasApplied)
  }, [hasApplied])

  return (
    <div className='flex flex-col items-start gap-10 w-full'>
      <div className='flex flex-col items-start gap-4'>
        <header className='flex flex-col items-start gap-1 w-full'>
          <div className='flex flex-col items-start w-full'>
            <Typography
              as='h5'
              className='text-jaa-shades-gray-2.5 sm font-normal'
            >
              {t('app.job_board.job_desc')}
            </Typography>
            <Typography
              as='h3'
              className='h5 lg:h3 font-medium text-jaa-shades-black'
            >
              {job?.title}
            </Typography>
          </div>
          <div className='flex flex-wrap items-center  gap-2 justify-start'>
            <Typography className='flex-1 sm text-jaa-shades-black font-normal'>
              {job?.company_name}
            </Typography>
            <span className='flex items-center gap-2'>
              <span className='w-0.5 h-0.5 rounded-full bg-jaa-shades-gray-2.5'></span>
              <span className='font-normal xs text-jaa-shades-gray-2.5'>
                {(job as any)?.company_industry}
              </span>
            </span>
            <Tooltip id='notification_tooltip'>
              <span className='flex flex-col text-black items-start rounded w-[320px] p-3 shadow-lg bg-white'>
                Turn on job notifications from this company to be notified of
                new opportunities!
              </span>
            </Tooltip>
            <span
              className='px-4 py-0.5 cursor-pointer'
              data-tooltip-id='notification_tooltip'
              data-tooltip-place='bottom'
              data-tooltip-offset={1}
              data-tooltip-wrapper='div'
              data-tooltip-variant={'light'}
              data-tooltip-position-strategy='absolute'
              onClick={() => {
                subScribeToJob({
                  jobId: job?.id!,
                  subScribed: !job?.receive_email!,
                })
              }}
            >
              {job?.receive_email ? (
                <svg
                  width='32'
                  height='32'
                  viewBox='0 0 32 32'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <rect
                    x='0.5'
                    y='0.5'
                    width='31'
                    height='31'
                    rx='15.5'
                    stroke='#F1F3F4'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M21.6243 14.0868V13.5C21.6243 10.2784 19.1062 7.66669 16 7.66669C12.8938 7.66669 10.3757 10.2784 10.3757 13.5V14.0868C10.3757 14.7909 10.1748 15.4793 9.79816 16.0652L8.8753 17.501C8.03235 18.8124 8.67587 20.5949 10.142 21.0097C13.9773 22.0945 18.0227 22.0945 21.858 21.0097C23.3241 20.5949 23.9676 18.8124 23.1247 17.501L22.2018 16.0652C21.8252 15.4793 21.6243 14.7909 21.6243 14.0868ZM16 24.3334C14.7618 24.3334 13.6607 23.7592 12.9598 22.8682C14.978 23.1417 17.022 23.1417 19.0402 22.8682C18.3393 23.7592 17.2382 24.3334 16 24.3334ZM19.6869 13.3067C19.8821 13.1114 19.8821 12.7949 19.6869 12.5996C19.4916 12.4043 19.175 12.4043 18.9798 12.5996L14.75 16.8294L13.0202 15.0996C12.825 14.9043 12.5084 14.9043 12.3131 15.0996C12.1179 15.2949 12.1179 15.6114 12.3131 15.8067L14.3964 17.89C14.5917 18.0853 14.9083 18.0853 15.1036 17.89L19.6869 13.3067Z'
                    fill='#2FB574'
                  />
                </svg>
              ) : (
                <svg
                  width='32'
                  height='32'
                  viewBox='0 0 32 32'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <rect
                    x='0.5'
                    y='0.5'
                    width='31'
                    height='31'
                    rx='15.5'
                    stroke='#F1F3F4'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M16.0001 7.04167C12.4333 7.04167 9.54178 9.93317 9.54178 13.5V14.0868C9.54178 14.6675 9.36987 15.2353 9.04772 15.7185L8.09055 17.1543C6.97964 18.8207 7.82773 21.0857 9.75989 21.6126C10.3896 21.7843 11.0246 21.9296 11.6632 22.0484L11.6648 22.0527C12.3056 23.7626 14.0184 24.9583 16.0001 24.9583C17.9817 24.9583 19.6945 23.7626 20.3353 22.0527L20.3369 22.0484C20.9756 21.9296 21.6106 21.7844 22.2403 21.6126C24.1725 21.0857 25.0206 18.8207 23.9097 17.1543L22.9525 15.7185C22.6304 15.2353 22.4584 14.6675 22.4584 14.0868V13.5C22.4584 9.93317 19.567 7.04167 16.0001 7.04167ZM18.8138 22.2808C16.9447 22.5041 15.0554 22.5041 13.1863 22.2808C13.7788 23.1321 14.8093 23.7083 16.0001 23.7083C17.1909 23.7083 18.2213 23.1321 18.8138 22.2808ZM10.7918 13.5C10.7918 10.6235 13.1236 8.29167 16.0001 8.29167C18.8766 8.29167 21.2084 10.6235 21.2084 13.5V14.0868C21.2084 14.9143 21.4534 15.7233 21.9124 16.4119L22.8696 17.8477C23.5073 18.8041 23.0205 20.1042 21.9114 20.4067C18.0412 21.4622 13.959 21.4622 10.0888 20.4067C8.97976 20.1042 8.49297 18.8041 9.13061 17.8477L10.0878 16.4119C10.5468 15.7233 10.7918 14.9143 10.7918 14.0868V13.5Z'
                    fill='#161B1D'
                  />
                </svg>
              )}
            </span>
          </div>
        </header>
        <main className='w-full flex flex-col items-start gap-3'>
          <div className='w-full gap-8 flex xl:gap-14 items-start'>
            <div className='flex items-center gap-2'>
              <iconComponents.util.TwoUserIcon className='w-[18px] h-[18px] stroke-jaa-shades-gray-2.5' />
              <Typography className='text-jaa-shades-black sm font-normal whitespace-nowrap'>
                {job?.company_size} employees
              </Typography>
            </div>
            <div className='flex items-center gap-2'>
              <iconComponents.util.LocationIcon className='w-[18px] h-[18px] stroke-jaa-shades-gray-2.5' />
              <Typography className='text-jaa-shades-black sm font-normal whitespace-nowrap'>
                {job?.location}
              </Typography>
            </div>
          </div>
          <div className='w-full gap-8 flex xl:gap-14 items-start '>
            <div className='flex items-center gap-2'>
              <iconComponents.profile.BriefCaseIcon className='w-[18px] h-[18px] stroke-jaa-shades-gray-2.5' />
              <Typography className='text-jaa-shades-black sm font-normal flex flex-1 gap-0.5 items-center flex-wrap'>
                <span
                  style={
                    JobTypeStyles[
                      job?.job_type
                        .replace(/[\s-]/, '_')
                        .toLowerCase() as 'internship'
                    ] || JobTypeStyles['full_time']
                  }
                  className='py-1.5 px-3 gap-2.5 flex justify-center items-center xs uppercase'
                >
                  {job?.job_type}
                </span>
                <span className='text-jaa-shades-gray-2.5'>
                  + {job?.years_of_experience}
                  {t('app.job_board.yrs_experience')}
                </span>
              </Typography>
            </div>
            <div className='flex items-center gap-2'>
              <iconComponents.profile.MoneyStackIcon className='w-[18px] h-[18px] stroke-jaa-shades-gray-2.5' />
              <Typography className='text-jaa-shades-black sm font-normal flex flex-1 gap-0.5 items-center flex-wrap'>
                {job?.job_type?.toLowerCase().includes('internship') ? (
                  <span>
                    {job.paid
                      ? `Paid (${job?.salary_range} ${
                          job?.salary_currency?.split?.(' ')?.[0]
                        })`
                      : 'Unpaid'}
                  </span>
                ) : (
                  <>
                    <span>{job?.salary_range}</span>
                    <span className='text-jaa-shades-gray-2.5'>
                      {job?.salary_currency?.split?.(' ')?.[0]}
                    </span>
                  </>
                )}
              </Typography>
            </div>
          </div>
          {job?.duration && (
            <div className='w-full gap-8 flex xl:gap-14 items-start'>
              <div className='flex items-center gap-2'>
                <iconComponents.profile.KeyOpenIcon className='w-[18px] h-[18px] stroke-jaa-shades-gray-2.5' />
                <Typography className='text-jaa-shades-black sm font-normal whitespace-nowrap'>
                  {job?.duration}
                </Typography>
              </div>
            </div>
          )}
        </main>
      </div>
      <JobImages data={job! || { images: [] }} isRecruiter={false} />
      {!isApplied && (
        <Button
          onClick={applyHandler}
          loading={isLoading}
          disabled={isLoading}
          label={t('app.apply')}
          className=' w-full md:w-[263px] !h-9'
        />
      )}
    </div>
  )
}

const NoDetailSelected: FC<{}> = () => {
  const { t } = useTranslation()

  return (
    <div className='min-h-[55vh] w-full h-full flex justify-center items-center flex-col bg-white rounded-lg self-stretch gap-6'>
      <iconComponents.recruitment.EmptyPageIcon className='w-[140px] h-[110px]' />
      <div className='flex flex-col gap-1 justify-center items-center'>
        <Typography as='h5' className='text-jaa-shades-black'>
          {t('app.recruitement.job_details')}
        </Typography>
        <Typography
          as='p'
          className='sm text-jaa-shades-gray-2.5 font-normal w-fu; text-center'
        >
          {t('app.recruitement.job_details_desc')}
        </Typography>
      </div>
    </div>
  )
}

export default JobDetail
